import { createContext } from 'react';
import { Id } from 'src/types/common';
import {
  AddApi,
  PatchApi,
  SelectionController,
  TableNavigator,
  TableStore
} from 'src/types/hooks';
import { AttachmentsTableContextType } from './attachments-table-context';
import { AttendeesTableContextType } from './attendees-table-context';
import { CategoriesTableContextType } from './categories-table-context';
import { CompaniesTableContextType } from './companies-table-context';
import { EmailTemplatesTableContextType } from './email-templates-table-context';
import { FilesTableContextType } from './files-table-context';
import { GroupsTableContextType } from './groups-table-context';
import { ListsTableContextType } from './lists-table-context';
import { MembersTableContextType } from './members-table-context';
import { MembershipTypesTableContextType } from './membership-types-table-context';
import { NewsletterItemsTableContextType } from './newsletter-items-table-context';
import { OccasionsTableContextType } from './occasions-table-context';
import { ParticipantsTableContextType } from './participants-table-context';
import { PostsTableContextType } from './posts-table-context';
import { RecipientsTableContextType } from './recipients-table-context';
import { RolesTableContextType } from './roles-table-context';
import { SignaturesTableContextType } from './signatures-table-context';
import { SurveyTemplatesTableContextType } from './survey-templates-table-context';
import { TagsTableContextType } from './tags-table-context';
import { WikiTableContextType } from './wiki-table-context';

/**
 * Provides re-usable table state management
 *
 * By using a unique interface, a lot of the UI code can be shared between
 * different table implementations. To increase re-usability, a single context
 * type is used, which can be accessed via standardized hooks.
 *
 * However, that means that there can not be more than one table context.
 */
export type TableContextType<
  Type,
  Store extends TableStore<object>,
  Api = undefined,
  Extra = undefined,
  Filters = object
> = {
  // unique type identifier for the table context
  type: Type;
  // navigator for table pagination and sorting
  navigator?: TableNavigator<Filters>;
  // selection controller for table row selection
  selection?: SelectionController;
  // table store for data and loading state
  store: Store;
  // access to underlying API
  api: Api;
  // additional table-specific data and actions
  extra: Extra;
};

export const TableContext = createContext<
  TableContextType<string, TableStore<object>, unknown, unknown> | undefined
>(undefined);

// table context type registry
export type TableContextTypes = {
  attachments: AttachmentsTableContextType;
  attendees: AttendeesTableContextType;
  categories: CategoriesTableContextType;
  companies: CompaniesTableContextType;
  emailTemplates: EmailTemplatesTableContextType;
  files: FilesTableContextType;
  groups: GroupsTableContextType;
  lists: ListsTableContextType;
  members: MembersTableContextType;
  membershipTypes: MembershipTypesTableContextType;
  newsletterItems: NewsletterItemsTableContextType;
  occasions: OccasionsTableContextType;
  participants: ParticipantsTableContextType;
  posts: PostsTableContextType;
  recipients: RecipientsTableContextType;
  roles: RolesTableContextType;
  signatures: SignaturesTableContextType;
  surveyTemplates: SurveyTemplatesTableContextType;
  tags: TagsTableContextType;
  wiki: WikiTableContextType;
};

/**
 * Default properties for table context providers
 */
export type PropsWithTableStore<
  T extends object,
  Type extends keyof TableContextTypes & string,
  Navigator = undefined,
  Api = undefined
> = T & {
  navigator?: Navigator;
  selection?: SelectionController;
  store: InferTableStore<Type>;
  api?: Api;
};
export type InferApi<T extends keyof TableContextTypes & string> =
  TableContextTypes[T]['api'];

export type InferTableStore<T extends keyof TableContextTypes & string> =
  TableContextTypes[T]['store'];

export type InferLocal<T extends keyof TableContextTypes & string> =
  TableContextTypes[T]['store'] extends TableStore<infer Local>
    ? Local & { id: Id }
    : { id: Id };

export type InferRemote<T extends keyof TableContextTypes & string> =
  TableContextTypes[T]['api'] extends PatchApi<infer Remote, object>
    ? Remote
    : never;

export type InferPatch<T extends keyof TableContextTypes & string> =
  TableContextTypes[T]['api'] extends PatchApi<object, infer Patch>
    ? Patch
    : never;

export type InferAdd<T extends keyof TableContextTypes & string> =
  TableContextTypes[T]['api'] extends AddApi<infer Add> ? Add : never;
